import { ColumnDef } from '@tanstack/react-table';
import { PencilSimple } from '@phosphor-icons/react';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Skill } from '../../types/skill.types';

interface CreateSkillTableColumnsProps {
  t: (key: string) => string;
  /** Sub-category title by id; a missing id means the sub-category was deleted. */
  subCategoryTitles: Record<string, string>;
  onEdit: (skill: Skill) => void;
  canUpdate?: boolean;
}

export const createSkillTableColumns = ({
  t,
  subCategoryTitles,
  onEdit,
  canUpdate = true,
}: CreateSkillTableColumnsProps): ColumnDef<Skill>[] => [
  {
    id: 'Name',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('SKILL_NAME')} />,
    accessorFn: (row) => row.Name ?? '',
    cell: ({ row }) => <span className="truncate font-medium">{row.original.Name}</span>,
  },
  {
    id: 'SubCategory',
    // A title looked up from SubCategoryId: no field to sort on; the toolbar filters by sub-category.
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('SUB_CATEGORY')} />,
    accessorFn: (row) => subCategoryTitles[row.SubCategoryId] ?? '',
    cell: ({ row }) => {
      const title = subCategoryTitles[row.original.SubCategoryId];
      return title ? (
        <span className="truncate">{title}</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  },
  ...(canUpdate
    ? [
        {
          id: 'actions',
          // Card: top right, as the old portal's card header.
          meta: { mobile: 'badge' as const },
          header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
          enableSorting: false,
          enableHiding: false,
          cell: ({ row }: { row: { original: Skill } }) => (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(row.original)}
              aria-label={t('EDIT')}
            >
              <PencilSimple className="size-4" />
            </Button>
          ),
        },
      ]
    : []),
];
